import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { propertiesApi, notificationsApi } from '../../lib/api';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import PWAInstallPrompt from '../PWAInstallPrompt';
import QuickActions from '../QuickActions';
import {
  CalendarDays,
  List,
  Receipt,
  Home,
  RefreshCw,
  Settings,
  LogOut,
  Menu,
  Bell,
  LayoutDashboard,
  Check,
  X,
  Users,
  Sparkles,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0e5f78cf-f454-4b25-b923-9c08cfb591e4/artifacts/lmgq6k8j_file_00000000ca9471f4a6a1a00d93bbed3f.png";

const navItems = [
  { to: '/admin/calendrier', icon: CalendarDays, label: 'Calendrier' },
  { to: '/admin/reservations', icon: List, label: 'Réservations' },
  { to: '/admin/reservations-voyageurs', icon: Users, label: 'Rés. Voyageurs', badge: true },
  { to: '/admin/depenses', icon: Receipt, label: 'Dépenses' },
  { to: '/admin/proprietes', icon: Home, label: 'Propriétés' },
  { to: '/admin/site-config', icon: Sparkles, label: 'Site & Marque' },
  { to: '/admin/synchronisation', icon: RefreshCw, label: 'Synchronisation' },
  { to: '/admin/tableau-de-bord', icon: LayoutDashboard, label: 'Tableau de bord' },
  { to: '/admin/parametres', icon: Settings, label: 'Paramètres' },
];

function SidebarNav({ onNavigate }) {
  return (
    <nav className="space-y-1 px-3">
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-all duration-200 group',
              isActive
                ? 'bg-primary/10 text-primary border border-primary/20'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            )
          }
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <item.icon className="h-4 w-4 transition-colors group-hover:text-primary" strokeWidth={1.5} />
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    loadProperties();
    loadNotifications();
  }, []);

  const loadProperties = async () => {
    try {
      const response = await propertiesApi.getAll();
      setProperties(response.data);
    } catch (error) {
      console.error('Error loading properties:', error);
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await notificationsApi.getAll();
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await notificationsApi.markRead(notificationId);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const markAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all read:', error);
    }
  };

  const initials = user?.name?.split(' ').map((n) => n[0]).join('').toUpperCase() || 'U';

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-0 z-40 hidden h-screen w-60 border-r border-border/40 bg-card md:flex md:flex-col">
        <div className="flex h-14 items-center gap-3 border-b border-border/40 px-4">
          <img src={LOGO_URL} alt="Era Lodge" className="h-8 w-auto" />
        </div>

        {/* Property Selector */}
        <div className="p-4 border-b border-border/40">
          <Select value={selectedProperty} onValueChange={setSelectedProperty}>
            <SelectTrigger className="w-full bg-muted/50 h-9 text-sm" data-testid="property-selector">
              <SelectValue placeholder="Toutes les propriétés" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les propriétés</SelectItem>
              {properties.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <ScrollArea className="flex-1 py-4">
          <SidebarNav />
        </ScrollArea>

        <div className="border-t border-border/40 p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-8 w-8 border border-border">
              <AvatarFallback className="bg-primary/10 text-primary text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-muted-foreground hover:text-destructive" data-testid="logout-button">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-50 flex h-14 items-center justify-between border-b border-border/40 bg-background/95 backdrop-blur px-4 md:hidden">
        <div className="flex items-center gap-3">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-60 p-0 bg-card flex flex-col">
              <div className="flex h-14 items-center gap-3 border-b border-border/40 px-4 shrink-0">
                <img src={LOGO_URL} alt="Era Lodge" className="h-8 w-auto" />
              </div>
              <div className="p-4 border-b border-border/40 shrink-0">
                <select 
                  value={selectedProperty} 
                  onChange={(e) => setSelectedProperty(e.target.value)}
                  className="w-full bg-muted/50 h-9 text-sm rounded-md border border-border px-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">Toutes les propriétés</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <ScrollArea className="flex-1 py-4">
                <SidebarNav onNavigate={() => setMobileOpen(false)} />
              </ScrollArea>
              <div className="border-t border-border/40 p-3 shrink-0">
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4" />
                  Déconnexion
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          <img src={LOGO_URL} alt="Era Lodge" className="h-7 w-auto" />
        </div>
        
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-primary">
                    {unreadCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
              <div className="flex items-center justify-between p-3 border-b border-border">
                <h4 className="font-medium text-sm">Notifications</h4>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" className="text-xs h-7" onClick={markAllRead}>
                    Tout marquer lu
                  </Button>
                )}
              </div>
              <ScrollArea className="h-[300px]">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground text-sm">
                    Aucune notification
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {notifications.slice(0, 10).map((notification) => (
                      <div 
                        key={notification.id} 
                        className={`p-3 ${!notification.read ? 'bg-primary/5' : ''}`}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{notification.title}</p>
                            <p className="text-xs text-muted-foreground mt-1">{notification.message}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(notification.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          {!notification.read && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6 shrink-0"
                              onClick={() => markNotificationRead(notification.id)}
                            >
                              <Check className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </PopoverContent>
          </Popover>
        </div>
      </header>

      {/* Main Content */}
      <main className="md:pl-60">
        <div className="min-h-screen pt-14 md:pt-0">
          <div className="p-4 md:p-6 lg:p-8">
            <Outlet context={{ selectedProperty, properties, loadProperties }} />
          </div>
        </div>
      </main>

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Quick Actions FAB (mobile only) */}
      <QuickActions />
    </div>
  );
}
